// Academy 147 logbook PDF layout.
import { createLogbookDoc, savePdf, groupTasksByChapter, sortChapterNumbers } from "./helpers";

const academy147Logo = require("@/assets/academy147_logo.png");
const ACADEMY_147_LOGO_ASPECT_RATIO = 1072 / 209; 

const BRAND_RED = [200, 16, 46];
const HEADER_GRAY = [128, 128, 128];
const SECTION_GRAY = [140, 140, 140];
const RULE_GRAY = [150, 150, 150];
const APPROVAL_HEADER_RED = [139, 0, 0];
const APPROVAL_ROW_PEACH = [250, 218, 185];
const VOID_GRAY = [150, 150, 150];

// Signature image height plus padding
const SIGNATURE_IMAGE_HEIGHT = 7;
const SIGNATURE_ROW_HEIGHT = 13;

// Task-table column widths (mm) and text styling, 
const TASK_TABLE_MARGIN = 10;
const TASK_CODE_COL_WIDTH = 14;
const MAN_OP_COL_WIDTH = 14;
const CAT_COL_WIDTH = 14;
const TASK_REF_COL_WIDTH = 38;
const AC_REG_COL_WIDTH = 24;
const DATE_COL_WIDTH = 24;
const CANDIDATE_SIG_COL_WIDTH = 25;
const INSTRUCTOR_SIG_COL_WIDTH = 25;
const TASK_TABLE_FONT_SIZE = 7.5;
const TASK_TABLE_CELL_PADDING = 1.5;
const TASK_TABLE_LINE_HEIGHT_FACTOR = 1.15;
const CHAPTER_BAR_ROW_HEIGHT = 9;
const TASK_COL_GROUP_DIVIDER_LINE_WIDTH = 0.6;

function taskDescriptionColumnWidth(pageWidth) {
    const fixedColumnsWidth = TASK_CODE_COL_WIDTH + MAN_OP_COL_WIDTH + CAT_COL_WIDTH
        + TASK_REF_COL_WIDTH + AC_REG_COL_WIDTH + DATE_COL_WIDTH
        + CANDIDATE_SIG_COL_WIDTH + INSTRUCTOR_SIG_COL_WIDTH;
    return pageWidth - TASK_TABLE_MARGIN * 2 - fixedColumnsWidth;
}

// "Issue 1, Revision 0" is static, but the month/year uses current time
function currentIssueRevisionText() {
    const now = new Date();
    const month = now.toLocaleString('en-US', { month: 'short' });
    return `Issue 1, Revision 0 - ${month} ${now.getFullYear()}`;
}

function drawAcademy147Logo(doc, x, y, width) {
    const height = width / ACADEMY_147_LOGO_ASPECT_RATIO;
    doc.addImage(academy147Logo, 'PNG', x, y, width, height);
    return height;
}

function drawAcademy147Footer(doc, context) {
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const totalPages = doc.internal.getNumberOfPages();
    const courseCode = context.product?.product_code || '';
    const productName = context.product?.name || '';
    const issueRevisionText = currentIssueRevisionText();

    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);

        doc.setDrawColor(...RULE_GRAY);
        doc.setLineWidth(0.3);
        doc.line(10, pageHeight - 19, pageWidth - 10, pageHeight - 19);

        drawAcademy147Logo(doc, 10, pageHeight - 16, 26);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(90, 90, 90);
        doc.text(`Course Code: ${courseCode}`, 42, pageHeight - 14);
        doc.text(productName, 42, pageHeight - 10, { maxWidth: 90 });

        doc.text(`© Copyright ${new Date().getFullYear()} - Academy 147 Ltd. All Rights Reserved`, pageWidth / 2, pageHeight - 14, { align: 'center' });
        doc.text('Logbook for Part-66 Type Practical Training', pageWidth / 2, pageHeight - 10, { align: 'center' });

        doc.text(`Page ${i} of ${totalPages}`, pageWidth - 10, pageHeight - 14, { align: 'right' });
        doc.text(issueRevisionText, pageWidth - 10, pageHeight - 10, { align: 'right' });

        doc.setTextColor(0, 0, 0);
        doc.setDrawColor(0, 0, 0);
    }
}

function drawTitleBlock(doc, y) {
    const pageWidth = doc.internal.pageSize.width;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...BRAND_RED);
    doc.text('Part-66 Aircraft Maintenance Type Training Logbook', pageWidth / 2, y, { align: 'center' });
    doc.text('For Aircraft Maintenance Personnel', pageWidth / 2, y + 8, { align: 'center' });
    doc.setTextColor(0, 0, 0);
}

function drawSectionBar(doc, label, top) {
    const pageWidth = doc.internal.pageSize.width;
    const height = 8;

    doc.setFillColor(...HEADER_GRAY);
    doc.rect(10, top, pageWidth - 20, height, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text(label, 13, top + height / 2, { baseline: 'middle' });
    doc.setTextColor(0, 0, 0);
}

function drawSectionHeading(doc, label, y) {
    const pageWidth = doc.internal.pageSize.width;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(13);
    doc.setTextColor(...SECTION_GRAY);
    doc.text(label, 10, y);

    doc.setDrawColor(...RULE_GRAY);
    doc.setLineWidth(1);
    doc.line(10, y + 3, pageWidth - 10, y + 3);

    doc.setTextColor(0, 0, 0);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.2);
}

function drawCoverPage(doc, context) {
    const pageWidth = doc.internal.pageSize.width;

    const logoWidth = 90;
    drawAcademy147Logo(doc, (pageWidth - logoWidth) / 2, 22, logoWidth);

    drawTitleBlock(doc, 65);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(...SECTION_GRAY);
    const productNameLines = doc.splitTextToSize(context.product?.name || '', 250);
    doc.text(productNameLines, pageWidth / 2, 88, { align: 'center' });
    doc.setTextColor(0, 0, 0);

    doc.autoTable({
        startY: 110,
        margin: { left: 10, right: 10 },
        theme: 'grid',
        styles: { lineColor: [0, 0, 0], lineWidth: 0.2, fontSize: 9, cellPadding: 2, valign: 'top' },
        body: [
            [
                {
                    content: 'Academy 147 Ltd. Practical Logbook Approval',
                    colSpan: 3,
                    styles: { fillColor: APPROVAL_HEADER_RED, textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: 11 },
                },
            ],
            [
                { content: 'Changes Requiring Approval by TM-CAD i.a.w. 1.10 and 1.11 of the MTOE', styles: { fontStyle: 'bold', halign: 'center' } },
                { content: 'Indirect Approval i.a.w. 1.12 of the MTOE', colSpan: 2, styles: { halign: 'center' } },
            ],
            [
                { content: 'Issue 1', styles: { fillColor: APPROVAL_ROW_PEACH, fontStyle: 'bold', halign: 'center' } },
                { content: 'Revision 0', colSpan: 2, styles: { fillColor: APPROVAL_ROW_PEACH, fontStyle: 'bold', halign: 'center' } },
            ],
            [
                { content: 'National Aviation Authority', styles: { fillColor: APPROVAL_ROW_PEACH, halign: 'center' } },
                { content: 'Academy 147', colSpan: 2, styles: { fillColor: APPROVAL_ROW_PEACH, halign: 'center' } },
            ],
            [
                { content: 'Approved by\n\n\n________________\nSignature\n\n\nDate:', styles: { minCellHeight: 38 } },
                { content: 'Approved by\n\n\n________________\nSignature\n\nLuke Formosa, Training Manager\nDate: 20/07/2026', styles: { minCellHeight: 38, textColor: VOID_GRAY } },
                { content: 'Approved by\n\n\n________________\nSignature\n\nConnie Di Cesare, Training Manager\nDate:', styles: { minCellHeight: 38, textColor: VOID_GRAY } },
            ],
        ],
        didDrawCell: (data) => {
            if (data.row.index === 4 && (data.column.index === 1 || data.column.index === 2)) {
                const { cell } = data;
                doc.setDrawColor(215, 215, 215);
                doc.setLineWidth(0.2);
                doc.line(cell.x, cell.y, cell.x + cell.width, cell.y + cell.height);
                doc.line(cell.x + cell.width, cell.y, cell.x, cell.y + cell.height);
                doc.setDrawColor(0, 0, 0);
            }
        },
    });
}

function drawIntroductionPage(doc) {
    doc.addPage();
    const pageWidth = doc.internal.pageSize.width;

    drawSectionBar(doc, 'INTRODUCTION', 14);
    drawTitleBlock(doc, 34);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const bodyMaxWidth = pageWidth - 20;
    let y = 54;

    const paragraph1 = 'This Logbook has been compiled by Academy 147 Ltd. MT.147.04 to comply with the following regulations: (EC) No. 1321/2014, CAR Part ii, Chapter 7, Singapore Airworthiness Requirements, and any other applicable approval, for the use in the delivery of Part-66 Practical Type Training.';
    doc.text(paragraph1, 10, y, { maxWidth: bodyMaxWidth, align: 'justify' });
    y += 10;

    const paragraph2 = 'The Maintenance Logbook is designed for candidates completing the practical component of type training. Candidates are required to document their training activities, which must be verified by the instructor or assessor to confirm the competencies achieved. Candidates must complete at least one task in every ATA chapter, and at least 50% of the mandatory items overall (as stipulated in Appendix III to Annex III (Part-66) section 3.2). All entries must be made in ink. The nature of each task should be specified using the following codes: LOC, FOT, SGH, R/I, MEL, TS. Refer to Appendix III to Part-66 for details on the Practical Element of Type Training.';
    doc.text(paragraph2, 10, y, { maxWidth: bodyMaxWidth, align: 'justify' });
    y += 24;

    drawSectionHeading(doc, 'Section 1 - Instructions for Use', y);
    y += 10;

    const leftColumnTop = y;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('General Information:', 10, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    [
        'All entries in the logbook shall be made in Blue or Black Ink',
        'Dates entered shall follow the format DD/MM/YY.',
        'When used in support of an application for a licence, any false entry in the logbook will constitute an offence under the legislation currently in force.',
    ].forEach(item => {
        doc.text('-', 14, y);
        doc.text(item, 20, y, { maxWidth: 165 });
        y += 4;
    });

    y += 7;
    doc.setFont('helvetica', 'bold');
    doc.text('Personal Details & Logbook Contains:', 10, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    [
        'Personal Details of Candidate',
        'Names of Assessors and Instructors',
        'Theoretical Element Part 147 Training Organisation',
        'Aircraft Maintenance Type Training Logbook',
    ].forEach(item => {
        doc.text('-', 14, y);
        doc.text(item, 20, y);
        y += 4;
    });

    doc.autoTable({
        startY: leftColumnTop,
        margin: { left: pageWidth - 90, right: 10 },
        tableWidth: 80,
        head: [[{ content: 'Task Codes', colSpan: 2, styles: { halign: 'center', fontStyle: 'bold' } }]],
        body: [
            ['LOC', 'Location'],
            ['FOT', 'Functional/Operational test'],
            ['SGH', 'Service and Ground Handling'],
            ['R/I', 'Removal / Installation'],
            ['MEL', 'Minimum Equipment List'],
            ['TS', 'Troubleshooting'],
        ],
        styles: { fontSize: 8, lineColor: [0, 0, 0], lineWidth: 0.2, cellPadding: 1.5 },
        headStyles: { fillColor: 255, textColor: 0 },
        columnStyles: { 0: { cellWidth: 15, fontStyle: 'bold' }, 1: { cellWidth: 65 } },
    });

    y = Math.max(y, doc.lastAutoTable.finalY) + 8;

    drawSectionHeading(doc, 'Section 2', y);
    y += 10;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Section 2 contains the Maintenance Tasks to be completed.', 10, y);
    y += 12;

    drawSectionHeading(doc, 'Section 3', y);
    y += 10;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Section 3 contains the Practical Assessment Result.', 10, y);
}

// Instruction line shown above a scope group's tasks when it has more than one 
function scopeGroupInstruction(scope, taskCount) {
    if (taskCount <= 1) return null;
    return `Complete at least one of the following ${scope} tasks.`;
}

// Builds the chapters/groups structure
function buildAtaChaptersFromTasks(context) {
    const groupedByChapter = groupTasksByChapter(context.filteredTasks);
    const sortedChaptNos = sortChapterNumbers(Object.keys(groupedByChapter));

    return sortedChaptNos.map(chaptNo => {
        const chapterTasks = groupedByChapter[chaptNo];
        const chapter = context.chapters.find(ch => ch.chapt_no === chaptNo);

        const groupOrder = [];
        const tasksByGroupKey = {};
        chapterTasks.forEach(task => {
            const scope = task.scope || 'Unspecified';
            const manOp = task.manob || '';
            const category = task.category || '';
            const groupKey = JSON.stringify([scope, manOp, category]);
            if (!tasksByGroupKey[groupKey]) {
                tasksByGroupKey[groupKey] = { scope, manOp, category, tasks: [] };
                groupOrder.push(groupKey);
            }
            tasksByGroupKey[groupKey].tasks.push(task);
        });

        const groups = groupOrder.map(groupKey => {
            const { scope, manOp, category, tasks } = tasksByGroupKey[groupKey];
            return {
                code: scope,
                manOp,
                category,
                instruction: scopeGroupInstruction(scope, tasks.length),
                tasks,
            };
        });

        return {
            ata_no: chaptNo,
            title: chapter?.name ? chapter.name.toUpperCase() : `CHAPTER ${chaptNo}`,
            groups,
        };
    });
}

// Only show the completion date once the task has actually been signed off
function formatCompletedDate(task) {
    if (task.completed == null || !task.selected_date) return '';
    return task.selected_date.split(' ')[0];
}

function buildTasksTableBody(chapters) {
    const body = [];
    const barRows = new Map();
    const crossCells = new Set();
    const rowTasks = new Map();
    const groupDividerRows = new Set();

    chapters.forEach(chapter => {
        barRows.set(body.length, `ATA ${chapter.ata_no}`);
        body.push([
            { content: chapter.title, colSpan: 9, styles: { fillColor: HEADER_GRAY, textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: 9 } },
        ]);

        chapter.groups.forEach((group, groupIndex) => {
            const groupRowSpan = (group.instruction ? 1 : 0) + group.tasks.length;
            let isFirstRowOfGroup = true;

            // Mark the first row of every group except the chapter's own
            // first group (that boundary is already the chapter bar row)
            if (groupIndex > 0) {
                groupDividerRows.add(body.length);
            }

            if (group.instruction) {
                const instructionRowIndex = body.length;
                crossCells.add(`${instructionRowIndex}:5`);
                crossCells.add(`${instructionRowIndex}:6`);
                crossCells.add(`${instructionRowIndex}:7`);
                crossCells.add(`${instructionRowIndex}:8`);
                body.push([
                    { content: group.code, rowSpan: groupRowSpan },
                    { content: group.manOp, rowSpan: groupRowSpan },
                    { content: group.category, rowSpan: groupRowSpan },
                    { content: group.instruction, colSpan: 2, styles: { halign: 'left' } },
                    '', '', '', '',
                ]);
                isFirstRowOfGroup = false;
            }

            group.tasks.forEach(task => {
                rowTasks.set(body.length, task);

                // Signature columns
                const candidateSignatureCell = { content: '', styles: { minCellHeight: SIGNATURE_ROW_HEIGHT } };
                const instructorSignStampCell = { content: '', styles: { minCellHeight: SIGNATURE_ROW_HEIGHT } };

                const row = isFirstRowOfGroup
                    ? [
                        { content: group.code, rowSpan: groupRowSpan },
                        { content: group.manOp, rowSpan: groupRowSpan },
                        { content: group.category, rowSpan: groupRowSpan },
                        task.reference,
                        task.description,
                        task.ac_reg || '',
                        formatCompletedDate(task),
                        candidateSignatureCell,
                        instructorSignStampCell,
                    ]
                    : [
                        task.reference,
                        task.description,
                        task.ac_reg || '',
                        formatCompletedDate(task),
                        candidateSignatureCell,
                        instructorSignStampCell,
                    ];
                body.push(row);
                isFirstRowOfGroup = false;
            });
        });
    });

    return { body, barRows, crossCells, rowTasks, groupDividerRows };
}

// Draws a signature image into a cell
function drawSignatureInCell(doc, cell, signatures, signatureId) {
    if (!signatureId) return;
    const selectedSignature = signatures.find(sig => sig.id === signatureId);
    const imageData = selectedSignature ? selectedSignature.user_image : null;
    if (!imageData) return;

    const imageWidth = Math.min(18, cell.width - 4);
    const imageHeight = SIGNATURE_IMAGE_HEIGHT;
    const imageX = cell.x + (cell.width - imageWidth) / 2;
    const imageY = cell.y + (cell.height - imageHeight) / 2;

    doc.addImage(imageData, 'PNG', imageX, imageY, imageWidth, imageHeight);
    doc.setLineWidth(0.25);
    doc.setDrawColor(0, 0, 0);
    doc.rect(imageX, imageY, imageWidth, imageHeight);
}

// This estimates, before any drawing happens, whether
// a chapter will fit in the space left on the page.
function estimateWrappedTextHeight(doc, text, maxWidth, fontSize) {
    if (!text) return 0;
    doc.setFontSize(fontSize);
    const lines = doc.splitTextToSize(String(text), maxWidth);
    const lineHeight = (fontSize * TASK_TABLE_LINE_HEIGHT_FACTOR) / doc.internal.scaleFactor;
    return lines.length * lineHeight;
}

function estimateTaskRowHeight(doc, task, descriptionColWidth) {
    const descriptionHeight = estimateWrappedTextHeight(doc, task.description, descriptionColWidth, TASK_TABLE_FONT_SIZE);
    const referenceHeight = estimateWrappedTextHeight(doc, task.reference, TASK_REF_COL_WIDTH, TASK_TABLE_FONT_SIZE);
    const contentHeight = Math.max(descriptionHeight, referenceHeight) + TASK_TABLE_CELL_PADDING * 2;

    return Math.max(contentHeight, SIGNATURE_ROW_HEIGHT);
}

function estimateInstructionRowHeight(doc, instructionText, instructionColWidth) {
    return estimateWrappedTextHeight(doc, instructionText, instructionColWidth, TASK_TABLE_FONT_SIZE) + TASK_TABLE_CELL_PADDING * 2;
}

// Sums the estimated height of every row a chapter's autoTable call will draw 
function estimateChapterHeight(doc, chapter, descriptionColWidth) {
    const instructionColWidth = TASK_REF_COL_WIDTH + descriptionColWidth;
    let height = CHAPTER_BAR_ROW_HEIGHT;

    chapter.groups.forEach(group => {
        if (group.instruction) {
            height += estimateInstructionRowHeight(doc, group.instruction, instructionColWidth);
        }
        group.tasks.forEach(task => {
            height += estimateTaskRowHeight(doc, task, descriptionColWidth);
        });
    });

    return height;
}

const TASK_TABLE_HEAD = [[
    { content: 'Task\nCode.', styles: { halign: 'center' } },
    { content: 'Man/\nOp.', styles: { halign: 'center' } },
    { content: 'Cat.', styles: { halign: 'center' } },
    'Task Reference.',
    'Task.',
    { content: 'Aircraft Reg.\nor MSN', styles: { halign: 'center' } },
    { content: 'Date Task\nCompleted.', styles: { halign: 'center' } },
    { content: 'Candidate\nSignature.', styles: { halign: 'center' } },
    { content: 'Instructor\nSign/Stamp.', styles: { halign: 'center' } },
]];

const TASK_TABLE_COLUMN_STYLES = {
    0: { cellWidth: TASK_CODE_COL_WIDTH, halign: 'center' },
    1: { cellWidth: MAN_OP_COL_WIDTH, halign: 'center' },
    2: { cellWidth: CAT_COL_WIDTH, halign: 'center' },
    3: { cellWidth: TASK_REF_COL_WIDTH },
    4: { cellWidth: 'auto' },
    5: { cellWidth: AC_REG_COL_WIDTH, halign: 'center' },
    6: { cellWidth: DATE_COL_WIDTH, halign: 'center' },
    7: { cellWidth: CANDIDATE_SIG_COL_WIDTH, halign: 'center' },
    8: { cellWidth: INSTRUCTOR_SIG_COL_WIDTH, halign: 'center' },
};

function drawTasksSection(doc, chapters, context) {
    doc.addPage();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const descriptionColWidth = taskDescriptionColumnWidth(pageWidth);
    const bottomMargin = 15;

    drawSectionBar(doc, 'SECTION 2 – MAINTENANCE TASKS', 14);

    let startY = 26;
    let needsHeader = true;

    chapters.forEach(chapter => {
        const estimatedHeight = estimateChapterHeight(doc, chapter, descriptionColWidth);

        // Keep the whole chapter together: if it won't fit in what's left
        // of this page, start it on a fresh one
        if (startY + estimatedHeight > pageHeight - bottomMargin) {
            doc.addPage();
            startY = 10;
            needsHeader = true;
        }

        const { body, barRows, crossCells, rowTasks, groupDividerRows } = buildTasksTableBody([chapter]);

        doc.autoTable({
            startY,
            margin: { left: 10, right: 10 },
            theme: 'grid',
            head: needsHeader ? TASK_TABLE_HEAD : undefined,
            body,
            styles: { fontSize: TASK_TABLE_FONT_SIZE, lineColor: [0, 0, 0], lineWidth: 0.2, cellPadding: TASK_TABLE_CELL_PADDING, valign: 'middle' },
            headStyles: { fillColor: HEADER_GRAY, textColor: 255, fontStyle: 'bold', halign: 'center' },
            columnStyles: TASK_TABLE_COLUMN_STYLES,
            didDrawCell: (data) => {
                if (data.section !== 'body') return;

                if (barRows.has(data.row.index) && data.column.index === 0) {
                    const { cell } = data;
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(9);
                    doc.setTextColor(255, 255, 255);
                    doc.text(barRows.get(data.row.index), cell.x + 2, cell.y + cell.height / 2, { baseline: 'middle' });
                    doc.setTextColor(0, 0, 0);
                    return;
                }

                // Draw a heavier line across the full row where one task
                // group ends and the next begins, so groups read as
                // visually separate while tasks within a group stay light.
                // Drawn once per row, on the last column's cell. Runs before
                // the crossCells check below so it still draws on rows whose
                // last column is a crossed-out cell (instruction rows).
                if (groupDividerRows.has(data.row.index) && data.column.index === 8) {
                    const { cell } = data;
                    doc.setLineWidth(TASK_COL_GROUP_DIVIDER_LINE_WIDTH);
                    doc.setDrawColor(...RULE_GRAY);
                    doc.line(TASK_TABLE_MARGIN, cell.y, pageWidth - TASK_TABLE_MARGIN, cell.y);
                    doc.setDrawColor(0, 0, 0);
                    doc.setLineWidth(0.2);
                }

                if (crossCells.has(`${data.row.index}:${data.column.index}`)) {
                    const { cell } = data;
                    doc.setDrawColor(190, 190, 190);
                    doc.setLineWidth(0.2);
                    doc.line(cell.x, cell.y, cell.x + cell.width, cell.y + cell.height);
                    doc.line(cell.x + cell.width, cell.y, cell.x, cell.y + cell.height);
                    doc.setDrawColor(0, 0, 0);
                    return;
                }

                if (data.column.index === 7 || data.column.index === 8) {
                    const task = rowTasks.get(data.row.index);
                    if (!task) return;
                    const signatureId = data.column.index === 7 ? task.t_signature_id : task.ins_signature_id;
                    drawSignatureInCell(doc, data.cell, context.signatures, signatureId);
                }
            },
        });

        startY = doc.lastAutoTable.finalY;
        needsHeader = false;
    });
}

// ---------------------------------------------------------------------
// Section 3 – Practical Assessment
// ---------------------------------------------------------------------

function collectAssessmentEntries(taskAssessments) {
    return (taskAssessments || []).flatMap(taskAssessment => taskAssessment.assessments || []);
}

// The overall result requires every assessment_task to have been attempted
// at least once, and every attempt recorded to have passed 
function isOverallAssessmentSatisfactory(taskAssessments) {
    if (!taskAssessments || taskAssessments.length === 0) return false;
    return taskAssessments.every(taskAssessment => {
        const attempts = taskAssessment.assessments || [];
        return attempts.length > 0 && attempts.every(attempt => attempt.result === 'pass');
    });
}

function mostRecentAssessmentEntry(entries) {
    return entries.reduce((latest, entry) => {
        if (!latest) return entry;
        return new Date(entry.date) > new Date(latest.date) ? entry : latest;
    }, null);
}

function drawWrappedText(doc, text, x, y, { maxWidth, fontSize = 9, lineHeightFactor = 1.15 } = {}) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(fontSize);
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, x, y);
    const lineHeight = (fontSize * lineHeightFactor) / doc.internal.scaleFactor;
    return lines.length * lineHeight;
}

function drawCheckbox(doc, x, y, size, checked) {
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.rect(x, y - size, size, size);
    if (checked) {
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.5);
        doc.setLineCap('round');
        doc.setLineJoin('round');
        doc.line(x + size * 0.15, y - size * 0.55, x + size * 0.4, y - size * 0.15);
        doc.line(x + size * 0.4, y - size * 0.15, x + size * 0.85, y - size * 0.8);
        doc.setLineWidth(0.2);
    }
}

function drawFormField(doc, label, value, x, y, lineEndX) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(label, x, y);
    const lineStartX = x + doc.getTextWidth(label) + 3;

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.2);
    doc.line(lineStartX, y + 0.5, lineEndX, y + 0.5);

    if (value) {
        doc.text(String(value), lineStartX + 2, y);
    }
}

function drawSignatureField(doc, label, signatureData, x, y, lineEndX) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(label, x, y);
    const lineStartX = x + doc.getTextWidth(label) + 3;

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.2);
    doc.line(lineStartX, y + 0.5, lineEndX, y + 0.5);

    if (signatureData) {
        const imageWidth = 24;
        const imageHeight = 8;
        doc.addImage(signatureData, 'PNG', lineStartX + 2, y - imageHeight - 0.5, imageWidth, imageHeight);
    }
}

function drawStampBox(doc, x, y, width, height) {
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.2);
    doc.rect(x, y, width, height);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(...SECTION_GRAY);
    doc.text('Stamp', x + width / 2, y + 6, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
}

function drawSection3PracticalAssessment(doc, context) {
    doc.addPage();
    const pageWidth = doc.internal.pageSize.width;
    const bodyMaxWidth = pageWidth - 20;
    const fieldLineEndX = 140;

    drawSectionBar(doc, 'SECTION 3 - PRACTICAL ASSESSMENT', 14);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(...BRAND_RED);
    doc.text('Part-66 Aircraft Maintenance Type Training Logbook for Aircraft Maintenance Personnel', pageWidth / 2, 32, { align: 'center' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('RESULT of PRACTICAL ASSESSMENT', pageWidth / 2, 41, { align: 'center' });
    doc.setTextColor(0, 0, 0);

    let y = 52;
    y += drawWrappedText(
        doc,
        'I certify that the practical assessment of the candidate, has been conducted in accordance with the European Parliament and of the Council and to Commission Regulation (EU) No 1321/2014, CAR II, Chapter 7, CAR-66 & Singapore Airworthiness Requirements Part-66.',
        10, y, { maxWidth: bodyMaxWidth }
    );
    y += 6;

    y += drawWrappedText(
        doc,
        'After evaluating the knowledge, skills, and attitude of the candidate, I certify that the practical assessment of the candidate:',
        10, y, { maxWidth: bodyMaxWidth }
    );
    y += 8;

    const entries = collectAssessmentEntries(context.taskAssessments);
    const isSatisfactory = isOverallAssessmentSatisfactory(context.taskAssessments);
    const isNotSatisfactory = entries.length > 0 && !isSatisfactory;

    const checkboxSize = 4;
    const checkboxTextX = 10 + checkboxSize + 4;
    const checkboxTextWidth = 190;

    drawCheckbox(doc, 10, y, checkboxSize, isSatisfactory);
    y += Math.max(
        drawWrappedText(
            doc,
            'is satisfactory, the candidate has passed this assessment and has gained the required competence in performing safe maintenance, inspections, and routine work on this aircraft type. I recommend him/her for type rating endorsement. *',
            checkboxTextX, y, { maxWidth: checkboxTextWidth }
        ),
        checkboxSize
    ) + 6;

    drawCheckbox(doc, 10, y, checkboxSize, isNotSatisfactory);
    const notSatisfactoryY = y;
    y += Math.max(
        drawWrappedText(
            doc,
            'is not satisfactory, the candidate has not passed this assessment and I do not recommend him/her for type rating endorsement. *',
            checkboxTextX, y, { maxWidth: checkboxTextWidth }
        ),
        checkboxSize
    );

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.text('* tick the applicable', checkboxTextX + checkboxTextWidth + 10, notSatisfactoryY);
    doc.setFont('helvetica', 'normal');

    y += 14;

    const mostRecentEntry = mostRecentAssessmentEntry(entries);
    const assessor = mostRecentEntry
        ? (context.instructors || []).find(inst => inst.user_id === mostRecentEntry.instructor_id)
        : null;
    const assessorName = assessor ? `${assessor.first_name} ${assessor.last_name}` : '';

    const assessorBlockTop = y;
    drawFormField(doc, 'Assessor Name:', assessorName, 10, y, fieldLineEndX);
    y += 12;
    drawSignatureField(doc, 'Assessor Signature:', mostRecentEntry?.assessor_signature, 10, y, fieldLineEndX);
    y += 12;
    drawFormField(doc, 'Date of Assessment Carried Out:', mostRecentEntry?.date || '', 10, y, fieldLineEndX);

    drawStampBox(doc, 150, assessorBlockTop - 8, 40, 35);

    y += 18;

    const candidateName = `${context.selectedTrainee.first_name} ${context.selectedTrainee.last_name}`;
    drawFormField(doc, "Candidate's Name:", candidateName, 10, y, fieldLineEndX);
    y += 12;
    drawSignatureField(doc, "Candidate's Signature:", mostRecentEntry?.trainee_signature, 10, y, fieldLineEndX);
    y += 12;
    drawFormField(doc, 'Date:', mostRecentEntry?.date || '', 10, y, fieldLineEndX);
}

export function generateAcademy147LogbookPdf(context) {
    const doc = createLogbookDoc({ orientation: 'landscape' });

    drawCoverPage(doc, context);
    drawIntroductionPage(doc);
    drawTasksSection(doc, buildAtaChaptersFromTasks(context), context);
    drawSection3PracticalAssessment(doc, context);

    drawAcademy147Footer(doc, context);

    savePdf(doc, context.logbookName);
}
